import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  grupoBenesView,
  olBenefitModel,
} from '../models/pacote-beneficio.models';
import { PropostaSalarialView } from '../models/proposta-salarial.model';
import { PropostaSalarialService } from './proposta-salarial.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class BeneficioService {
  private dominioId: string;
  pacoteView: PropostaSalarialView = {};
  private pacoteBenesPath: string;
  private salario: number;
  private sexo: string;
  private optionalBenefitsSelection: Record<string, boolean>;

  private allBenefits: { [benefitTypeId: string]: olBenefitModel[] };
  private benefits: { [benefitTypeId: string]: string[] };
  private salarioLimiteByBenefitId: { [benefitId: string]: number };
  private optionalByBenefitId: { [benefitId: string]: boolean };
  private genderByBenefitId: { [benefitId: string]: string };
  // benefícios marcados `isRemuneracao` no catálogo (ex.: PLRE) — não entram
  // na lista de benefícios, são exibidos à parte no bloco de Remuneração.
  remuneracaoBenefits: olBenefitModel[];

  constructor(
    private firestore: AngularFirestore,
    private authService: AuthService,
    propostaService: PropostaSalarialService
  ) {
    this.dominioId = this.authService.dominioId;
    this.pacoteBenesPath = propostaService.getView().pacoteBenes;
    this.salario = propostaService.getView().salario;
    this.sexo = propostaService.getView().sexo;
    this.optionalBenefitsSelection = propostaService.getView().optionalBenefitsSelection ?? {};
    this.allBenefits = {};
    this.benefits = {};
    this.salarioLimiteByBenefitId = {};
    this.optionalByBenefitId = {};
    this.genderByBenefitId = {};
    this.remuneracaoBenefits = [];
  }

  async load() {
    const pacote = await firstValueFrom(
      this.firestore.doc(`${this.pacoteBenesPath}`).get()
    );

    if (!pacote.exists) {
      return;
    }
    const pac = pacote.data() as any;

    Object.keys(pac.benefits).forEach((grupo) => {
      this.allBenefits[grupo] = [];
      this.benefits[grupo] = [];
      const benes = pac.benefits[grupo];

      for (const bene of benes) {
        this.benefits[grupo].push(bene.value);
        if (bene.salarioLimite != null) {
          this.salarioLimiteByBenefitId[bene.value] = bene.salarioLimite;
        }
        if (bene.optional) {
          this.optionalByBenefitId[bene.value] = true;
        }
        if (bene.conditionGender) {
          this.genderByBenefitId[bene.value] = bene.conditionGender;
        }
      }
    });
  }

  async loadBenes() {
    return this.firestore
      .collection<olBenefitModel>(`empresaMae/${this.dominioId}/olBenefit`)
      .ref // .where('status', '==', 'ativo')
      .get()
      .then((bns) => {
        if (bns.empty) {
          return null;
        }
        const listGrupos = [];
        Object.keys(this.benefits).forEach((e) => {
          listGrupos.push(e);
        });

        bns.docs.forEach((p) => {
          const bene: olBenefitModel = p.data();
          if (bene.status !== 'ativo') {
            return;
          }

          // benefício configurado com um limite salarial no pacote: some da
          // proposta quando o salário atinge esse valor (mesma regra do
          // preview interno em hr-admin, agora aplicada no app do candidato).
          const salarioLimite = this.salarioLimiteByBenefitId[p.id];
          if (salarioLimite != null && this.salario != null && this.salario >= salarioLimite) {
            return;
          }

          // benefício opcional: só aparece se o RH respondeu explicitamente "sim"
          // ao montar a proposta. Sem resposta (bug, edge case) = não aparece
          // (fail-closed) — mais seguro para um benefício com critério de
          // elegibilidade sensível (ex.: auxílio-creche).
          if (this.optionalByBenefitId[p.id] && this.optionalBenefitsSelection[p.id] !== true) {
            return;
          }

          // benefício exclusivo por gênero: só aparece quando o sexo da
          // proposta bate exatamente com o configurado (também exclui quando
          // sexo não foi preenchido) — resolvido automaticamente, sem
          // depender de `optionalBenefitsSelection`.
          const conditionGender = this.genderByBenefitId[p.id];
          if (conditionGender && conditionGender !== this.sexo) {
            return;
          }

          if (
            listGrupos.indexOf(bene.benefitTypeId) !== -1 &&
            this.benefits[bene.benefitTypeId].includes(p.id)
          ) {
            // benefício marcado como parte da remuneração no catálogo (ex.:
            // PLRE/participação nos resultados) — sai da lista de
            // benefícios, vai pro bloco de Remuneração.
            if (bene.isRemuneracao) {
              this.remuneracaoBenefits.push(bene);
            } else {
              this.allBenefits[bene.benefitTypeId].push(bene);
            }
          }
        });
      });
  }

  async getGrupos() {
    await this.load();
    await this.loadBenes();

    const grupos = [];

    return this.firestore
      .collection<grupoBenesView>(`empresaMae/${this.dominioId}/olBenefitType`)
      .ref // .where('status', '==', 'ativo')
      .get()
      .then((types) => {
        if (types.empty) {
          return null;
        }
        types.docs.forEach((p) => {
          const grupo: grupoBenesView = p.data();

          if (grupo.status !== 'ativo') {
            return;
          }

          const listGrupos = [];
          Object.keys(this.benefits).forEach((e) => {
            listGrupos.push(e);
          });

          if (listGrupos.indexOf(p.ref.id) !== -1 && this.allBenefits[p.ref.id]?.length) {
            grupos.push({
              id: p.ref.id,
              ...grupo,
              benes: [...this.allBenefits[p.ref.id]].sort(this.byOrdem),
            });
          }
        });
        // grupos com item de menor `ordem` aparecem primeiro; grupos sem
        // nenhum item ordenado mantêm a posição relativa atual, no fim
        // (sort estável).
        return grupos.sort(
          (a, b) => this.minOrdem(a.benes) - this.minOrdem(b.benes)
        );
      });
  }

  /** `true` se algum benefício da lista (fora os `isRemuneracao`) tem `ordem` configurado. */
  hasCustomOrder(): boolean {
    return Object.values(this.allBenefits)
      .flat()
      .some((bene) => bene.ordem != null);
  }

  /** Lista plana de todos os benefícios do pacote (fora `isRemuneracao`), ordenada só por `ordem`,
   * ignorando categoria — usado só quando `hasCustomOrder()` é `true` (ex.: PDF). */
  getFlatSortedBenefits(): olBenefitModel[] {
    return Object.values(this.allBenefits).flat().sort(this.byOrdem);
  }

  private byOrdem(a: olBenefitModel, b: olBenefitModel): number {
    const ordemA = a.ordem ?? Number.MAX_SAFE_INTEGER;
    const ordemB = b.ordem ?? Number.MAX_SAFE_INTEGER;
    return ordemA - ordemB;
  }

  private minOrdem(benes: olBenefitModel[]): number {
    return Math.min(...benes.map((bene) => bene.ordem ?? Number.MAX_SAFE_INTEGER));
  }
}
